import type React from "react";
import { useEffect, useMemo, useState } from "react";
import {
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Typography,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import { searchStandbyOrdersApi } from "../../api/standbyOrdersApi";
import { updateUangJalanReportOrdersApi } from "../../api/uangJalanReportsApi";
import { TestId } from "../../constants/testIds";
import { OrderStatus } from "../../types/order";
import type { StandbyOrder } from "../../types/standbyOrder";
import type { UangJalanReport } from "../../types/uangJalanReport";
import type translations from "../../translations/translations.json";
import { getErrorMessage } from "../../utils/errorHandling";
import { toastEmitter } from "../../utils/toastEmitter";

type TranslationKeys = keyof typeof translations.en;

interface AddStandbyOrdersToReportDialogProps {
  report: UangJalanReport;
  open: boolean;
  onClose: () => void;
  onSuccess: () => Promise<void>;
}

const AddStandbyOrdersToReportDialog: React.FC<AddStandbyOrdersToReportDialogProps> = ({
  report,
  open,
  onClose,
  onSuccess,
}) => {
  const { t } = useTranslation();
  const [availableOrders, setAvailableOrders] = useState<StandbyOrder[]>([]);
  const [selectedOrderIds, setSelectedOrderIds] = useState<string[]>([]);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const existingIds = useMemo(() => new Set(report.standbyOrderIds), [report.standbyOrderIds]);

  useEffect(() => {
    if (!open) {
      setSelectedOrderIds([]);
      setError(null);
      return;
    }

    const loadOrders = async () => {
      setLoading(true);
      setError(null);
      try {
        const { standbyOrders } = await searchStandbyOrdersApi(
          {
            teamOperationsId: report.teamOperationsId,
            status: OrderStatus.READY_FOR_REVIEW,
          },
          { page: 1, pageSize: 100 },
        );
        setAvailableOrders(
          standbyOrders.filter((order) => !existingIds.has(order._id) && !order.uangJalanReportId),
        );
      } catch (err: any) {
        setError(getErrorMessage(err, t, "fetchStandbyOrders"));
      } finally {
        setLoading(false);
      }
    };

    loadOrders();
  }, [open, report.teamOperationsId, existingIds, t]);

  const handleSubmit = async () => {
    if (selectedOrderIds.length === 0) {
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      await updateUangJalanReportOrdersApi(report._id, {
        standbyOrderIds: [...report.standbyOrderIds, ...selectedOrderIds],
      });
      await onSuccess();
      toastEmitter.emit(t("reports.addStandbySuccessfully" as TranslationKeys), "success");
      onClose();
    } catch (err: any) {
      const errorMessage = getErrorMessage(err, t, "updateUangJalanReport");
      setError(errorMessage);
      toastEmitter.emit(errorMessage, "error");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="sm"
      data-testid={TestId.UJR.ADD_STANDBY_DIALOG}
    >
      <DialogTitle>{t("reports.addStandbyTitle" as TranslationKeys)}</DialogTitle>
      <DialogContent>
        {loading ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 3 }}>
            <CircularProgress size={24} />
          </Box>
        ) : availableOrders.length === 0 ? (
          <Typography variant="body2" color="text.secondary">
            {t("reports.noStandbyToAdd" as TranslationKeys)}
          </Typography>
        ) : (
          <Box sx={{ display: "flex", flexDirection: "column" }}>
            {availableOrders.map((order) => (
              <FormControlLabel
                key={order._id}
                control={
                  <Checkbox
                    checked={selectedOrderIds.includes(order._id)}
                    onChange={() =>
                      setSelectedOrderIds((current) =>
                        current.includes(order._id)
                          ? current.filter((id) => id !== order._id)
                          : [...current, order._id],
                      )
                    }
                    slotProps={{
                      input: {
                        "data-testid": TestId.UJR.ADD_STANDBY_CHECKBOX(order._id),
                      } as React.InputHTMLAttributes<HTMLInputElement>,
                    }}
                  />
                }
                label={`${order.readableId} · ${order.driver?.name || "—"}`}
              />
            ))}
          </Box>
        )}
        {error && (
          <Typography color="error" sx={{ mt: 2 }}>
            {error}
          </Typography>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>{t("Cancel" as TranslationKeys)}</Button>
        <Button
          variant="contained"
          disabled={submitting || selectedOrderIds.length === 0}
          onClick={handleSubmit}
          data-testid={TestId.UJR.ADD_STANDBY_SUBMIT}
        >
          {t("reports.addStandby" as TranslationKeys)}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AddStandbyOrdersToReportDialog;
